import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  createErrorMessage,
  toCreateBody,
  validateGiftCardForm,
  type GiftCardFormErrors,
  type GiftCardFormValues,
} from './create-gift-card-form';
import { createGiftCard } from './giftCardsApi';

const EMPTY: GiftCardFormValues = { amount: '', recipientEmail: '', reason: '' };

/** Form state and submit for creating a gift card; opens the new card on success. */
export function useGiftCardCreate() {
  const navigate = useNavigate();
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState<GiftCardFormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const set = (key: keyof GiftCardFormValues) => (value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const found = validateGiftCardForm(values);
    setErrors(found);
    setSubmitError(null);
    if (Object.keys(found).length > 0) return;
    setSubmitting(true);
    try {
      const { giftCard, emailSent } = await createGiftCard(toCreateBody(values));
      navigate(`/admin/gift-cards/${giftCard.id}`, {
        state: emailSent ? undefined : { emailFailedFor: values.recipientEmail.trim() },
      });
    } catch (err) {
      setSubmitError(createErrorMessage(err));
      setSubmitting(false);
    }
  };

  return { values, errors, submitError, submitting, set, submit };
}
